import { useState } from 'react';
import { FileStack, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePropertyKitTemplates, usePropertyKitTemplateItems } from '@/hooks/inventory';
import { usePropertyArticles } from '@/hooks/inventory/usePropertyArticles';

interface AddFromTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
}

export default function AddFromTemplateModal({
  open,
  onOpenChange,
  propertyId,
}: AddFromTemplateModalProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const { templates, isLoading: isLoadingTemplates } = usePropertyKitTemplates();
  const { templateItems } = usePropertyKitTemplateItems(selectedTemplateId || undefined);
  const { addFromTemplate, isApplyingTemplate } = usePropertyArticles(propertyId);

  const handleApply = async () => {
    if (!selectedTemplateId) return;

    try {
      await addFromTemplate(selectedTemplateId);
      onOpenChange(false);
      setSelectedTemplateId('');
    } catch (error) {
      console.error('Error applying template:', error);
    }
  };

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileStack className="h-5 w-5" />
            Agregar desde plantilla
          </DialogTitle>
          <DialogDescription>
            Selecciona una plantilla para agregar todos sus artículos a esta propiedad.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Plantilla</Label>
            <Select
              value={selectedTemplateId}
              onValueChange={setSelectedTemplateId}
              disabled={isLoadingTemplates}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona una plantilla" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTemplate && (
            <div className="rounded-lg bg-muted p-3 space-y-2">
              <p className="text-sm font-medium">{selectedTemplate.name}</p>
              {selectedTemplate.description && (
                <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
              )}
              <p className="text-sm text-muted-foreground">
                {templateItems.length} artículo{templateItems.length !== 1 ? 's' : ''} en esta plantilla
              </p>
            </div>
          )}

          {templates.length === 0 && !isLoadingTemplates && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No hay plantillas disponibles. Crea una desde el botón "Plantillas" en la pestaña Kits.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleApply}
            disabled={!selectedTemplateId || isApplyingTemplate}
            className="bg-brand-primary hover:bg-brand-primary-hover"
          >
            {isApplyingTemplate && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Aplicar plantilla
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
